import { useId, useMemo, useState } from 'react';
import { ArrowRight, Check, Plus, Trash } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { Id, MONO_CLASS } from '../../ui/Mono';
import { Skeleton } from '../../ui/Skeleton';
import { FormatLocationPlain } from '../collector/collectorScan';
import RouteComparator from '../serviceorders/RouteComparator';
import {
    AllocationItemsOf,
    IndexJourneyLegs,
    IsCompleteRow,
    OrderIndexByLegKey,
    RouteItemsOfPreview,
} from './allocationModel';

/**
 * UMA LEVA da alocação: as linhas LPN→destino, a PRÉVIA da JORNADA de cada
 * pallet (com as OSs que sairiam), o comparador de rotas e a confirmação.
 *
 * ── A JORNADA (M5 r1) ───────────────────────────────────────────────────────
 * O pallet não "vai de A para B": ele percorre PERNAS — sai do rack para a
 * cabeceira da rua, é transportado até a cabeceira da rua de destino e sobe
 * para a posição. Cada perna é um item de OS. Aéreo→aéreo, que o modelo antigo
 * RECUSAVA com 400 ("faça em duas alocações"), é uma jornada normal.
 *
 * A tela mostra as pernas, quem ENTREGA para quem (os handoffs) e oferece a
 * decisão que é do Supervisor: **juntar tudo numa OS só** — um equipamento
 * fazendo a jornada inteira. Isso é botão de primeira classe, nunca erro.
 *
 * ── Digitar o código é o caminho principal ──────────────────────────────────
 * O wire do M5 fala CÓDIGO de endereço e de LPN — é o que o Supervisor lê no
 * mapa e o que o operador bipa. Os campos são de texto livre, com sugestões
 * (datalist) quando o usuário tem permissão para listar LPNs/localizações.
 * Quem tem só `wms:manage:service_orders` continua alocando normalmente,
 * digitando; a tela não vira refém de uma permissão que não é a dela.
 *
 * ── A prévia não grava ──────────────────────────────────────────────────────
 * `POST /allocation/preview` é consulta. Enquanto o Supervisor não apertar
 * "Gerar OSs", nada existe no banco — e é por isso que fatiar é uma operação
 * de bancada: mexer aqui é grátis, desfazer OS gerada não é.
 */

/** Chip do endereço de uma ponta da perna. */
const Code = ({ value }) => <Id value={value} className="text-[11px]" />;

/**
 * A JORNADA de UM pallet: as pernas na ordem em que acontecem, cada uma com o
 * comportamento e a OS em que caiu. Quando duas pernas seguidas caem em OSs
 * diferentes, é ali que nasce o handoff — e a tela diz isso na própria linha.
 */
const JourneyLine = ({ line, label, orderIndexByKey, orderCount }) => {
    const text = (key) => label(key);
    return (
        <li className="rounded border border-mono-200 bg-white p-2 dark:border-mono-800 dark:bg-mono-950">
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <Code value={line.lpn_code} />
                {line.direction ? (
                    <Badge variant="neutral">{text(`wms.allocation.direction.${line.direction}`)}</Badge>
                ) : null}
                {line.split ? <Badge variant="info">{text(`wms.allocation.split.${line.split}`)}</Badge> : null}
            </div>
            <ol className="space-y-1">
                {line.legs.map((leg) => {
                    const orderIndex = orderIndexByKey.get(leg.key);
                    return (
                        <li
                            key={leg.key}
                            className="flex flex-wrap items-center gap-1.5 text-[11px] text-mono-600 dark:text-mono-400"
                        >
                            <span className="font-semibold text-mono-500 dark:text-mono-400">
                                {`${text('wms.allocation.leg')} ${leg.leg}`}
                            </span>
                            <span>{text(`wms.allocation.leg.${leg.leg}`)}</span>
                            <Code value={leg.from_location_code} />
                            <ArrowRight size={11} className="shrink-0" />
                            <Code value={leg.to_location_code} />
                            {leg.behavior_key ? (
                                <Badge variant="default">{label(`wms.behavior.${leg.behavior_key}`)}</Badge>
                            ) : null}
                            {/* Em qual OS esta perna cai. Só faz sentido dizer
                                quando existe mais de uma — numa OS só, "OS 1"
                                seria ruído. */}
                            {orderCount > 1 && orderIndex !== undefined ? (
                                <span className="font-mono">{`OS ${orderIndex + 1}`}</span>
                            ) : null}
                        </li>
                    );
                })}
            </ol>
        </li>
    );
};

const AllocationBatchCard = ({
    batch,
    index,
    total,
    label,
    lpnSuggestions = [],
    locationSuggestions = [],
    onChangeRow,
    onAddRow,
    onRemoveRow,
    onSplit,
    onPreview,
    onToggleMerge,
    onChooseRoute,
    onConfirm,
    confirming = false,
}) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const lpnListId = useId();
    const locationListId = useId();

    const items = useMemo(() => AllocationItemsOf(batch), [batch]);
    const routeItems = useMemo(() => RouteItemsOfPreview(batch.preview), [batch.preview]);
    // Em qual OS cada PERNA caiu — é o que mostra, na própria jornada, onde o
    // trabalho troca de equipamento (e, portanto, de onde vem a espera).
    const orderIndexByKey = useMemo(() => OrderIndexByLegKey(batch.preview), [batch.preview]);
    const legByKey = useMemo(() => IndexJourneyLegs(batch.preview), [batch.preview]);
    const completeRows = batch.rows.filter(IsCompleteRow).length;
    const canSplit = selectedRows.length > 0 && selectedRows.length < batch.rows.length;

    const toggleRow = (rowKey) => {
        setSelectedRows((previous) =>
            previous.includes(rowKey) ? previous.filter((key) => key !== rowKey) : [...previous, rowKey],
        );
    };

    const locationByCode = useMemo(() => {
        const map = new Map();
        for (const row of locationSuggestions) {
            map.set(String(row.code || '').toUpperCase(), row);
        }
        return map;
    }, [locationSuggestions]);

    return (
        <section className="space-y-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
            {/* Sem título repetido: a página já se chama "Alocar carga". A
                identidade da leva é o contador — e ele só aparece quando há
                mais de uma (uma leva só não é "1 de 1", é a alocação). */}
            {total > 1 || batch.result ? (
                <header className="flex flex-wrap items-center justify-between gap-2">
                    {total > 1 ? <Badge variant="neutral">{`${index + 1}/${total}`}</Badge> : <span />}
                    {batch.result ? <Badge variant="success">{label('wms.service_order.status.READY')}</Badge> : null}
                </header>
            ) : null}

            {/* Leva já confirmada: vira recibo. Reabrir para editar seria
                oferecer desfazer o que o banco já gravou. */}
            {batch.result ? (
                <ul className="space-y-1">
                    {(batch.result.service_orders ?? batch.result.results ?? []).map((order) => (
                        <li key={order.id ?? order.code} className="flex items-center gap-2 text-sm">
                            <Check size={14} className="text-success-600" />
                            <Id value={order.code} className="font-medium" />
                            <span className="text-mono-600 dark:text-mono-400">
                                {order.type_label || order.type_code}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <>
                    <div className="space-y-2">
                        {batch.rows.map((row) => {
                            const destination = locationByCode.get(String(row.to_location_code || '').toUpperCase());
                            return (
                                <div key={row.key} className="flex flex-wrap items-start gap-2">
                                    <input
                                        type="checkbox"
                                        aria-label={label('wms.allocation.split')}
                                        checked={selectedRows.includes(row.key)}
                                        onChange={() => toggleRow(row.key)}
                                        className="mt-3 h-4 w-4 shrink-0 accent-black dark:accent-white"
                                    />
                                    <div className="min-w-[180px] flex-1">
                                        <Input
                                            label={label('wms.collector.lpn_label')}
                                            value={row.lpn_code}
                                            list={lpnListId}
                                            autoComplete="off"
                                            className={MONO_CLASS}
                                            onChange={(event) =>
                                                onChangeRow(row.key, { lpn_code: event.target.value.toUpperCase() })
                                            }
                                        />
                                    </div>
                                    <div className="min-w-[180px] flex-1">
                                        <Input
                                            label={label('wms.collector.to')}
                                            value={row.to_location_code}
                                            list={locationListId}
                                            autoComplete="off"
                                            className={MONO_CLASS}
                                            onChange={(event) =>
                                                onChangeRow(row.key, {
                                                    to_location_code: event.target.value.toUpperCase(),
                                                })
                                            }
                                        />
                                        {destination ? (
                                            <p className="mt-1 truncate text-[11px] text-mono-500 dark:text-mono-400">
                                                {FormatLocationPlain(destination, { label })}
                                            </p>
                                        ) : null}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="mt-6"
                                        disabled={batch.rows.length <= 1}
                                        onClick={() => onRemoveRow(row.key)}
                                        aria-label={label('wms.lpn_create.remove_item')}
                                    >
                                        <Trash size={16} />
                                    </Button>
                                </div>
                            );
                        })}

                        <datalist id={lpnListId}>
                            {lpnSuggestions.map((lpn) => (
                                <option key={lpn.id ?? lpn.code} value={lpn.code}>
                                    {lpn.location_code || ''}
                                </option>
                            ))}
                        </datalist>
                        <datalist id={locationListId}>
                            {locationSuggestions.map((location) => (
                                <option key={location.id ?? location.code} value={location.code}>
                                    {FormatLocationPlain(location, { label })}
                                </option>
                            ))}
                        </datalist>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" size="sm" onClick={onAddRow}>
                            <Plus size={14} className="mr-1.5" />
                            {label('wms.lpn_create.add_item')}
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={!canSplit}
                            onClick={() => {
                                onSplit(selectedRows);
                                setSelectedRows([]);
                            }}
                        >
                            {label('wms.allocation.split')}
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            loading={batch.previewing}
                            disabled={completeRows === 0}
                            onClick={onPreview}
                        >
                            {label('wms.allocation.preview')}
                        </Button>
                    </div>

                    {batch.previewError ? (
                        <p className="rounded-lg border border-danger-300 bg-danger-soft p-3 text-sm text-danger-800 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-300">
                            {batch.previewError}
                        </p>
                    ) : null}

                    {batch.previewing && !batch.preview ? <Skeleton className="h-24 w-full" /> : null}

                    {batch.preview ? (
                        <div className="space-y-3">
                            {/* A decisão do Supervisor sobre a jornada, ANTES
                                das OSs: juntar tudo num equipamento só (M5-1)
                                é opção de primeira classe. Ela vai na PRÓPRIA
                                prévia — a lista abaixo tem de ser exatamente o
                                que o "Gerar OSs" vai criar. */}
                            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-mono-200 bg-mono-50 p-3 dark:border-mono-800 dark:bg-mono-900/40">
                                <Button
                                    variant={batch.mergeIntoSingleOrder ? 'primary' : 'secondary'}
                                    size="sm"
                                    aria-pressed={Boolean(batch.mergeIntoSingleOrder)}
                                    loading={batch.previewing}
                                    onClick={onToggleMerge}
                                >
                                    {label('wms.allocation.merge')}
                                </Button>
                                <p className="min-w-[220px] flex-1 text-xs text-mono-600 dark:text-mono-400">
                                    {batch.preview.merged
                                        ? label('wms.allocation.merge_hint')
                                        : label('wms.allocation.split_default')}
                                </p>
                                {batch.preview.merged ? (
                                    <Badge variant="info">
                                        {label('wms.allocation.merge_on')}
                                    </Badge>
                                ) : null}
                            </div>

                            {/* A JORNADA de cada pallet: as pernas na ordem em
                                que acontecem. É o que substitui o antigo
                                "par vertical + transporte" — que só sabia
                                descrever 1 salto por pallet. */}
                            {batch.preview.lines.length > 0 ? (
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-mono-600 dark:text-mono-400">
                                        {label('wms.allocation.journey')}
                                    </p>
                                    <ul className="space-y-2">
                                        {batch.preview.lines.map((line) => (
                                            <JourneyLine
                                                key={`${line.seq}-${line.lpn_code}`}
                                                line={line}
                                                label={label}
                                                orderIndexByKey={orderIndexByKey}
                                                orderCount={batch.preview.orders.length}
                                            />
                                        ))}
                                    </ul>
                                </div>
                            ) : null}

                            {/* As trocas de mão: os ÚNICOS pontos de espera da
                                jornada. Sem handoff não existe par nem tela
                                âmbar — e é isso que o merge elimina. */}
                            {batch.preview.handoffs.length > 0 ? (
                                <div className="rounded-lg border border-warning-300 bg-warning-soft p-3 dark:border-warning-800 dark:bg-warning-900/20">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-warning-800 dark:text-warning-300">
                                        {label('wms.allocation.handoff')}
                                    </p>
                                    <ul className="mt-1 space-y-0.5">
                                        {batch.preview.handoffs.map((handoff) => {
                                            // A troca de mão contada como o
                                            // Supervisor a lê: qual pallet, qual
                                            // perna entrega, qual recebe e ONDE.
                                            const from = legByKey.get(handoff.from_key);
                                            const to = legByKey.get(handoff.to_key);
                                            return (
                                                <li
                                                    key={`${handoff.from_key}-${handoff.to_key}`}
                                                    className="flex flex-wrap items-center gap-1.5 text-[11px] text-warning-800 dark:text-warning-300"
                                                >
                                                    <Code value={handoff.location_code} />
                                                    {from?.line?.lpn_code ? <Code value={from.line.lpn_code} /> : null}
                                                    <span>
                                                        {`${label('wms.allocation.leg')} ${from?.leg?.leg ?? handoff.from_key}`}
                                                    </span>
                                                    <ArrowRight size={11} className="shrink-0" />
                                                    <span>
                                                        {`${label('wms.allocation.leg')} ${to?.leg?.leg ?? handoff.to_key}`}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <p className="mt-1 text-[11px] text-warning-800 dark:text-warning-300">
                                        {label('wms.allocation.handoff_hint')}
                                    </p>
                                </div>
                            ) : null}

                            {/* As OSs que SAIRIAM, na ordem em que receberiam os
                                códigos (quem começa o trabalho vem primeiro). */}
                            <div className="grid gap-3 md:grid-cols-2">
                                {batch.preview.orders.map((order, orderIndex) => (
                                    <div
                                        key={order.key}
                                        className="rounded border border-mono-200 bg-white p-2 dark:border-mono-800 dark:bg-mono-950"
                                    >
                                        {/* O distintivo de comportamento só entra
                                            quando ACRESCENTA: tipo cadastrado com
                                            o mesmo nome do comportamento (o caso
                                            do seed) mostraria a mesma palavra
                                            duas vezes. */}
                                        <p className="mb-1 flex flex-wrap items-center gap-2 text-sm font-medium text-black dark:text-white">
                                            {batch.preview.orders.length > 1 ? (
                                                <span className="font-mono text-xs text-mono-500 dark:text-mono-400">
                                                    {`OS ${orderIndex + 1}`}
                                                </span>
                                            ) : null}
                                            {order.type_label || order.type_code}
                                            {order.behavior_key &&
                                            label(`wms.behavior.${order.behavior_key}`) !==
                                                (order.type_label || order.type_code) ? (
                                                <Badge variant="info">
                                                    {label(`wms.behavior.${order.behavior_key}`)}
                                                </Badge>
                                            ) : null}
                                        </p>
                                        <ol className="space-y-0.5">
                                            {order.items.map((item) => (
                                                <li
                                                    key={item.key}
                                                    className="flex flex-wrap items-center gap-1 text-[11px] text-mono-600 dark:text-mono-400"
                                                >
                                                    <Id value={item.seq} className="text-[11px]" />
                                                    <Code value={item.from_location_code} />
                                                    <ArrowRight size={11} className="shrink-0" />
                                                    <Code value={item.to_location_code} />
                                                    {item.lpn_code ? <Code value={item.lpn_code} /> : null}
                                                    {/* Item que ESPERA: a espera
                                                        do par tem nome e lugar
                                                        antes de o operador bipar. */}
                                                    {item.handoff_from_key ? (
                                                        <Badge variant="warning">
                                                            {label('wms.allocation.handoff')}
                                                        </Badge>
                                                    ) : null}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                ))}
                            </div>

                            {routeItems.length > 0 ? (
                                <RouteComparator
                                    items={routeItems}
                                    label={label}
                                    chosenAlgorithm={batch.routeAlgorithm}
                                    onChoose={onChooseRoute}
                                />
                            ) : null}

                            {batch.error ? (
                                <p className="rounded-lg border border-danger-300 bg-danger-soft p-3 text-sm text-danger-800 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-300">
                                    {batch.error}
                                </p>
                            ) : null}

                            <Button
                                variant="primary"
                                loading={confirming}
                                disabled={items.length === 0}
                                onClick={onConfirm}
                            >
                                <Check size={16} className="mr-1.5" />
                                {label('wms.allocation.confirm')}
                            </Button>
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
};

export default AllocationBatchCard;
